import { gql, useMutation } from "@outschool/ui-apollo";
import {
  FollowedTeachers,
  followUserMutation
} from "@outschool/ui-components-website";
import React from "react";

import { validOptimisticResponse } from "../../shared/client/ApolloClient";
import { isDevelopment } from "../../shared/Env";
import {
  FollowUserMutation,
  FollowUserMutationVariables,
  FollowedTeachersQuery
} from "../../shared/generated/graphql";

interface IngorableError {
  name?: string;
}

function isIgnorableStoreUpdateError(
  isDevelopment: boolean,
  e?: IngorableError
) {
  if (isDevelopment) {
    return false;
  } else if (e && e.name) {
    return ["Invariant Violation"].includes(e.name);
  }
  return false;
}

export function useFollowUserMutation() {
  const [mutate] = useMutation<FollowUserMutation, FollowUserMutationVariables>(
    followUserMutation
  );

  return React.useCallback(
    (leader: $TSFixMe, referer: $TSFixMe, attribution: $TSFixMe) => {
      mutate({
        variables: {
          userUid: leader.uid,
          referer,
          attribution
        },
        optimisticResponse: validOptimisticResponse({
          __typename: "Mutation",
          followUser: {
            __typename: "FollowedUser",
            uid: new Date(),
            followed_uid: leader.uid,
            created_at: new Date(),
            leader: {
              __typename: "Leader",
              ...leader
            }
          }
        }),
        update(store, result) {
          if (!result.data) {
            return;
          }
          try {
            const data = store.readQuery<FollowedTeachersQuery>({
              query: FollowedTeachers
            });
            if (data?.currentUser?.followedUsers) {
              store.writeQuery({
                query: FollowedTeachers,
                data: {
                  ...data,
                  currentUser: {
                    ...data.currentUser,
                    followedUsers: [
                      ...data.currentUser.followedUsers,
                      result.data.followUser
                    ]
                  }
                }
              });
            }
          } catch (e) {
            if (!isIgnorableStoreUpdateError(isDevelopment, e)) {
              throw e;
            }
          }
        }
      });
    },
    [mutate]
  );
}

const unfollowUserMutation = gql`
  mutation UnfollowUser($userUid: ID!) {
    unfollowUser(userUid: $userUid)
  }
`;

export function useUnfollowUserMutation() {
  const [mutate] = useMutation(unfollowUserMutation);

  return React.useCallback(
    (userUid: $TSFixMe) => {
      mutate({
        variables: {
          userUid
        },
        optimisticResponse: validOptimisticResponse({
          __typename: "Mutation",
          unfollowUser: true
        }),
        update(store) {
          const data = store.readQuery<FollowedTeachersQuery>({
            query: FollowedTeachers
          });
          if (!data?.currentUser) {
            return;
          }
          const followedUsers = data.currentUser.followedUsers.filter(
            fu => fu.leader.uid !== userUid
          );
          store.writeQuery({
            query: FollowedTeachers,
            data: {
              ...data,
              currentUser: { ...data.currentUser, followedUsers }
            }
          });
        }
      });
    },
    [mutate]
  );
}
